/**
 * The equipment form — `docs/record-flyouts.md` §5, on the `Sheet` and the
 * field kit from `24-record-flyouts/html/flyout-field-kit.html`.
 *
 * One form for a piece that exists and one that does not: they ask the same
 * questions, and a truck bought last week and a truck being corrected today
 * are the same record with the same rules.
 *
 * §5's five sections, in the order somebody knows the answers in — **What it
 * is** · **Where it lives** · **What it costs** · **Books** · **Service** —
 * plus a sixth for the note. §5 does not name a Notes section, but `notes` is
 * a real column somebody types into and the old form had a bare `<textarea>`
 * hanging under the last section with no label of its own; a section is the
 * honest home for it.
 *
 * The page-local `Select` is gone. So is `<input type="date">`: plate expiry
 * and the purchase date are `DateInput`, which is 36px and reads
 * "Sep 12, 2026" on every platform rather than three different controls.
 *
 * Money is asked for once, in dollars, and sent as a number; the screens read
 * it back formatted (§A.1).
 */
import { useEffect, useMemo, useState } from 'react'
import { Sheet } from '../sheet.js'
import { Band, Button } from '../primitives.js'
import {
  ComboInput, DateInput, Field, FormGrid, FormSection, MoneyInput, NumberInput,
  SelectInput, TextArea, TextInput, Wide,
} from '../form/index.js'
import { OrgUnitPicker, type OrgUnit } from '../scope-tree.js'
import { request } from '../../lib/api.js'
import { IconCheck, IconMachine } from '../../frame/icons.js'
import type { EquipmentDetail } from '../../lib/equipment-types.js'

interface Lookups {
  types: { id: string; name: string; family: string | null; meterKind: string }[]
  templates: { id: string; name: string }[]
  yards: { id: string; name: string }[]
  crews: { id: string; name: string }[]
  pieces: { id: string; name: string; assetTag: string }[]
  orgUnits: OrgUnit[]
}

/** Every field the form holds, as strings — an input has nothing else. */
interface FormState {
  name: string
  /** Customer-facing name (0126); blank is "the same as the name". */
  customerName: string
  assetTag: string
  equipmentTypeId: string
  orgUnitId: string
  homeYardId: string
  serviceTemplateId: string
  year: string
  make: string
  model: string
  vin: string
  plate: string
  plateExpiresOn: string
  gvwrLb: string
  curbWeightLb: string
  purchasedOn: string
  purchaseCost: string
  salvageValue: string
  taxMethod: string
  bookMethod: string
  bookLifeMonths: string
  internalHourlyRate: string
  billableRate: string
  billableUnit: string
  notes: string
  crewId: string
  pairedWithId: string
}

const EMPTY: FormState = {
  name: '', customerName: '', assetTag: '', equipmentTypeId: '', orgUnitId: '', homeYardId: '',
  serviceTemplateId: '', year: '', make: '', model: '', vin: '', plate: '',
  plateExpiresOn: '', gvwrLb: '', curbWeightLb: '', purchasedOn: '', purchaseCost: '',
  salvageValue: '', taxMethod: 'none', bookMethod: 'straight_line', bookLifeMonths: '60',
  internalHourlyRate: '', billableRate: '', billableUnit: 'day', notes: '',
  crewId: '', pairedWithId: '',
}

/**
 * What a second piece keeps.
 *
 * Equipment arrives in twos and threes — three of the same mower, a run of
 * trailers off one invoice — so the type, the branch, the yard, the service
 * schedule and the rates stay. The name, the tag, the VIN and the plate are
 * what makes them different, so they clear.
 */
const CARRIED: (keyof FormState)[] = [
  'equipmentTypeId', 'orgUnitId', 'homeYardId', 'serviceTemplateId', 'make', 'model', 'year',
  'taxMethod', 'bookMethod', 'bookLifeMonths', 'internalHourlyRate', 'billableRate',
  'billableUnit',
]

export interface EquipmentFormProps {
  open: boolean
  onClose: () => void
  /** The branch a new piece belongs to until somebody says otherwise. */
  orgUnitId: string
  /** Present when editing; absent when adding. */
  existing?: EquipmentDetail | null
  /** `again` is Save & add another: stay open, keep what the next one shares. */
  onSaved: (equipmentId: string, again?: boolean) => void
}

export function EquipmentForm({
  open, onClose, orgUnitId, existing, onSaved,
}: EquipmentFormProps) {
  const [form, setForm] = useState<FormState>(EMPTY)
  const [saved, setSaved] = useState<FormState>(EMPTY)
  const [scope, setScope] = useState<string[]>([])
  const [lookups, setLookups] = useState<Lookups | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [carried, setCarried] = useState(false)

  useEffect(() => {
    if (!open) return
    void request<Lookups>('/equipment/lookups')
      .then(setLookups)
      .catch(() => setLookups(null))
  }, [open])

  useEffect(() => {
    if (!open) return
    setProblem(null)
    setErrors({})
    setCarried(false)

    if (!existing) {
      const fresh = { ...EMPTY, orgUnitId }
      setForm(fresh)
      setSaved(fresh)
      setScope([])
      return
    }

    const piece = existing.equipment
    const fresh: FormState = {
      name: piece.name,
      customerName: piece.customerName ?? '',
      assetTag: piece.assetTag,
      equipmentTypeId: piece.equipmentTypeId ?? '',
      orgUnitId: piece.orgUnitId,
      homeYardId: piece.homeYardId ?? '',
      serviceTemplateId: piece.serviceTemplateId ?? '',
      year: piece.year === null ? '' : String(piece.year),
      make: piece.make ?? '',
      model: piece.model ?? '',
      vin: piece.vin ?? '',
      plate: piece.plate ?? '',
      plateExpiresOn: piece.plateExpiresOn ?? '',
      gvwrLb: piece.gvwrLb === null ? '' : String(piece.gvwrLb),
      curbWeightLb: piece.curbWeightLb === null ? '' : String(piece.curbWeightLb),
      purchasedOn: piece.purchasedOn ?? '',
      purchaseCost: trim(piece.purchaseCost),
      salvageValue: trim(piece.salvageValue),
      taxMethod: piece.taxMethod,
      bookMethod: piece.bookMethod,
      bookLifeMonths: String(piece.bookLifeMonths),
      internalHourlyRate: trim(piece.internalHourlyRate),
      billableRate: trim(piece.billableRate),
      billableUnit: piece.billableUnit,
      notes: piece.notes,
      crewId: existing.livesWith?.id ?? '',
      pairedWithId: existing.pairedWith[0]?.id ?? '',
    }
    setForm(fresh)
    setSaved(fresh)
    setScope(existing.scope.map((unit) => unit.orgUnitId))
  }, [open, existing, orgUnitId])

  const set = (part: Partial<FormState>) => {
    setForm((current) => ({ ...current, ...part }))
    setErrors((current) => {
      const keys = Object.keys(part)
      return Object.fromEntries(Object.entries(current).filter(([key]) => !keys.includes(key)))
    })
  }

  const changes = useMemo(
    () => (Object.keys(form) as (keyof FormState)[])
      .filter((key) => form[key] !== saved[key]).length,
    [form, saved])

  /** A piece is never paired with itself, whatever the list says. */
  const pairable = useMemo(
    () => (lookups?.pieces ?? []).filter((piece) => piece.id !== existing?.equipment.id),
    [lookups, existing])

  const chosenType = useMemo(
    () => (lookups?.types ?? []).find((type) => type.id === form.equipmentTypeId) ?? null,
    [lookups, form.equipmentTypeId])

  /**
   * The type lookup.
   *
   * Search only: there is no route that makes an equipment type, so offering
   * "Create …" here would be a row that quietly does nothing. The empty line
   * says where they are set up instead.
   */
  const searchTypes = useMemo(() => (query: string) => {
    const wanted = query.trim().toLowerCase()
    return (lookups?.types ?? [])
      .filter((type) => type.name.toLowerCase().includes(wanted))
      .slice(0, 8)
      .map((type) => ({
        id: type.id,
        name: type.name,
        sub: [type.family, meterWords(type.meterKind)].filter(Boolean).join(' · '),
      }))
  }, [lookups])

  function check(next: FormState): Record<string, string> {
    const found: Record<string, string> = {}
    if (!next.name.trim()) found.name = 'A piece needs a name the crew would use'
    if (!next.assetTag.trim()) found.assetTag = 'The tag is how it is found in the yard'
    return found
  }

  async function save(again: boolean) {
    const found = check(form)
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setBusy(true)
    setProblem(null)
    try {
      const body = {
        name: form.name.trim(),
        customerName: form.customerName.trim() || null,
        assetTag: form.assetTag.trim(),
        equipmentTypeId: form.equipmentTypeId || null,
        orgUnitId: form.orgUnitId || orgUnitId,
        homeYardId: form.homeYardId || null,
        serviceTemplateId: form.serviceTemplateId || null,
        year: number(form.year),
        make: form.make.trim() || null,
        model: form.model.trim() || null,
        vin: form.vin.trim() || null,
        plate: form.plate.trim() || null,
        plateExpiresOn: form.plateExpiresOn || null,
        gvwrLb: number(form.gvwrLb),
        curbWeightLb: number(form.curbWeightLb),
        purchasedOn: form.purchasedOn || null,
        purchaseCost: number(form.purchaseCost) ?? 0,
        salvageValue: number(form.salvageValue) ?? 0,
        taxMethod: form.taxMethod,
        bookMethod: form.bookMethod,
        bookLifeMonths: number(form.bookLifeMonths) ?? 60,
        internalHourlyRate: number(form.internalHourlyRate),
        billableRate: number(form.billableRate),
        billableUnit: form.billableUnit.trim() || 'day',
        notes: form.notes.trim(),
        scopeOrgUnitIds: scope,
        crewId: form.crewId || null,
        pairedWithId: form.pairedWithId || null,
      }

      const written = existing
        ? await request<{ id: string }>(`/equipment/${existing.equipment.id}`, {
          method: 'PUT',
          body: { ...body, version: existing.equipment.version },
        })
        : await request<{ id: string }>('/equipment', { method: 'POST', body })

      if (again && !existing) {
        const next = { ...EMPTY, orgUnitId }
        for (const key of CARRIED) next[key] = form[key]
        setForm(next)
        setSaved(next)
        setCarried(true)
        onSaved(written.id, true)
        return
      }
      onSaved(written.id)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  const count = Object.keys(errors).length
  const units = lookups?.orgUnits ?? []

  return (
    <Sheet
      open
      onClose={onClose}
      kind="equipment"
      title={existing ? (form.name || 'Equipment') : 'Add equipment'}
      sub="Equipment · a piece the yard keeps track of"
      dirty={changes > 0}
      changeCount={changes}
      saveLabel={existing ? 'Save equipment' : 'Add it'}
      onSave={() => void save(false)}
      note={count > 0
        ? (
          <span className="text-danger">
            {count === 1 ? 'One field needs attention' : `${count} fields need attention`}
          </span>
        )
        : undefined}
      secondary={!existing
        ? (
          <Button className="h-[34px]" disabled={busy} onClick={() => void save(true)}>
            Save &amp; add another
          </Button>
        )
        : undefined}
      primary={(
        <Button
          variant="primary"
          className="h-[34px]"
          disabled={busy}
          onClick={() => void save(false)}
        >
          <IconCheck size={15} />
          {busy ? 'Saving…' : existing ? 'Save equipment' : 'Add it'}
        </Button>
      )}
    >
      {problem && <Band tone="amber">{problem}</Band>}
      {carried && (
        <Band tone="green">
          Added. The type, the branch, the yard and the rates stayed for the next one.
        </Band>
      )}

      {/* --- what it is ------------------------------------------------- */}
      <FormSection title="What it is" />
      <FormGrid>
        <Field label="Name" required error={errors.name} hint="what the crew calls it out loud">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              value={form.name}
              placeholder="Truck 22"
              onChange={(event) => set({ name: event.target.value })}
            />
          )}
        </Field>

        <Field label="Asset tag" required error={errors.assetTag} hint="what is on the sticker">
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              describedBy={describedBy}
              invalid={invalid}
              value={form.assetTag}
              placeholder="T-22"
              onChange={(event) => set({ assetTag: event.target.value })}
            />
          )}
        </Field>

        <Wide>
          <Field
            label="Type"
            hint={chosenType
              ? `${meterWords(chosenType.meterKind)} — that is what the crew reads every morning`
              : 'the type carries the meter — miles, hours or neither'}
          >
            {({ id, describedBy }) => (
              <ComboInput
                id={id}
                describedBy={describedBy}
                label="Type"
                thing="equipment type"
                placeholder="Search types…"
                value={chosenType ? { id: chosenType.id, name: chosenType.name } : null}
                onPick={(picked) => set({ equipmentTypeId: picked.id })}
                search={searchTypes}
                empty="No type matches that. Types are set up in the catalog."
              />
            )}
          </Field>
        </Wide>

        <Field label="Make" optional>
          {({ id }) => (
            <TextInput
              id={id}
              value={form.make}
              placeholder="Ford"
              onChange={(event) => set({ make: event.target.value })}
            />
          )}
        </Field>

        <Field label="Model" optional>
          {({ id }) => (
            <TextInput
              id={id}
              value={form.model}
              placeholder="F-350"
              onChange={(event) => set({ model: event.target.value })}
            />
          )}
        </Field>

        <Field label="Year" optional>
          {({ id }) => (
            <NumberInput
              id={id}
              value={form.year}
              placeholder="2021"
              onChange={(event) => set({ year: event.target.value })}
            />
          )}
        </Field>

        <Field label="VIN" optional>
          {({ id }) => (
            <TextInput
              id={id}
              value={form.vin}
              onChange={(event) => set({ vin: event.target.value.toUpperCase() })}
            />
          )}
        </Field>

        <Field label="Plate" optional>
          {({ id }) => (
            <TextInput
              id={id}
              value={form.plate}
              onChange={(event) => set({ plate: event.target.value.toUpperCase() })}
            />
          )}
        </Field>

        <Field label="Plate expires" optional>
          {({ id }) => (
            <DateInput
              id={id}
              label="Plate expires"
              value={form.plateExpiresOn}
              onChange={(value) => set({ plateExpiresOn: value })}
            />
          )}
        </Field>

        <Field label="GVWR" optional>
          {({ id }) => (
            <NumberInput
              id={id}
              unit="lb"
              value={form.gvwrLb}
              onChange={(event) => set({ gvwrLb: event.target.value })}
            />
          )}
        </Field>

        <Field
          label="Curb weight"
          optional
          hint="what a pairing adds up — over 26,000 lb combined needs a CDL"
        >
          {({ id, describedBy }) => (
            <NumberInput
              id={id}
              describedBy={describedBy}
              unit="lb"
              value={form.curbWeightLb}
              onChange={(event) => set({ curbWeightLb: event.target.value })}
            />
          )}
        </Field>
      </FormGrid>

      {/* --- where it lives --------------------------------------------- */}
      <FormSection title="Where it lives" />
      <FormGrid>
        <Field label="Branch">
          {({ id }) => (
            <SelectInput
              id={id}
              label="Branch"
              value={form.orgUnitId || null}
              placeholder="Pick a branch"
              onChange={(value) => set({ orgUnitId: value ?? '' })}
              options={units.map((one) => ({ value: one.id, label: one.name }))}
            />
          )}
        </Field>

        <Field label="Home yard" optional>
          {({ id }) => (
            <SelectInput
              id={id}
              label="Home yard"
              value={form.homeYardId || null}
              placeholder="No yard"
              clearable
              clearLabel="No yard"
              onChange={(value) => set({ homeYardId: value ?? '' })}
              options={(lookups?.yards ?? []).map((yard) => ({
                value: yard.id, label: yard.name,
              }))}
            />
          )}
        </Field>

        <Wide>
          <Field
            label="Lives with a crew"
            optional
            hint="their own piece is added to their morning automatically, whatever the day’s service says"
          >
            {({ id, describedBy }) => (
              <SelectInput
                id={id}
                describedBy={describedBy}
                label="Lives with a crew"
                value={form.crewId || null}
                placeholder="Nobody in particular"
                clearable
                clearLabel="Nobody in particular"
                onChange={(value) => set({ crewId: value ?? '' })}
                options={(lookups?.crews ?? []).map((crew) => ({
                  value: crew.id, label: crew.name,
                }))}
              />
            )}
          </Field>
        </Wide>

        <Wide>
          <OrgUnitPicker
            units={units}
            selected={scope}
            onChange={setScope}
            label="Where it may go"
            variant="chips"
            everywhereHint="Any crew in its own branch can take it. That is the normal case — widen it only for something that genuinely travels."
          />
        </Wide>
      </FormGrid>

      {/* --- what it costs ---------------------------------------------- */}
      <FormSection title="What it costs" />
      <FormGrid>
        <Field label="Purchase cost" optional>
          {({ id }) => (
            <MoneyInput
              id={id}
              value={form.purchaseCost}
              placeholder="0.00"
              onChange={(event) => set({ purchaseCost: event.target.value })}
            />
          )}
        </Field>

        <Field label="Purchased on" optional>
          {({ id }) => (
            <DateInput
              id={id}
              label="Purchased on"
              value={form.purchasedOn}
              onChange={(value) => set({ purchasedOn: value })}
            />
          )}
        </Field>

        <Field
          label="Internal hourly rate"
          optional
          hint="what an hour on this piece costs a job"
        >
          {({ id, describedBy }) => (
            <MoneyInput
              id={id}
              describedBy={describedBy}
              unit="/h"
              value={form.internalHourlyRate}
              placeholder="0.00"
              onChange={(event) => set({ internalHourlyRate: event.target.value })}
            />
          )}
        </Field>

        {/*
          * What a customer pays, and what they read, is the kind's — one row on
          * the catalog's Equipment tab for every machine of the type (0129) —
          * because a quote names a kind and operations sends whichever is free.
          * The machine keeps its internal rate: that is what an hour of *this*
          * one costs a job, and it is what the cost ledger posts.
          */}
        <Wide>
          <p className="m-0 text-[12px] text-text-muted" data-priced-by-kind="">
            {chosenType
              ? `Priced on the catalog’s Equipment tab as ${chosenType.name}, once for every machine of that kind.`
              : 'Give it a type to quote it: a quote names the kind of machine, priced on the catalog’s Equipment tab.'}
          </p>
        </Wide>
      </FormGrid>

      {/* --- books ------------------------------------------------------ */}
      <FormSection
        title="Books"
        sub="two sets, because they answer to different people: tax to the return, book to the job’s cost"
      />
      <FormGrid>
        <Field label="Tax depreciation">
          {({ id }) => (
            <SelectInput
              id={id}
              label="Tax depreciation"
              value={form.taxMethod}
              onChange={(value) => set({ taxMethod: value ?? 'none' })}
              options={[
                { value: 'none', label: 'None' },
                { value: 'macrs_5', label: 'MACRS 5-year' },
                { value: 'macrs_7', label: 'MACRS 7-year' },
                { value: 'straight_line', label: 'Straight line' },
              ]}
            />
          )}
        </Field>

        <Field label="Book depreciation">
          {({ id }) => (
            <SelectInput
              id={id}
              label="Book depreciation"
              value={form.bookMethod}
              onChange={(value) => set({ bookMethod: value ?? 'none' })}
              options={[
                { value: 'none', label: 'None' },
                { value: 'straight_line', label: 'Straight line' },
              ]}
            />
          )}
        </Field>

        <Field label="Book life">
          {({ id }) => (
            <NumberInput
              id={id}
              unit="months"
              value={form.bookLifeMonths}
              onChange={(event) => set({ bookLifeMonths: event.target.value })}
            />
          )}
        </Field>

        <Field label="Salvage value" optional hint="what it is worth at the end of that life">
          {({ id, describedBy }) => (
            <MoneyInput
              id={id}
              describedBy={describedBy}
              value={form.salvageValue}
              placeholder="0.00"
              onChange={(event) => set({ salvageValue: event.target.value })}
            />
          )}
        </Field>
      </FormGrid>

      {/* --- service ---------------------------------------------------- */}
      <FormSection title="Service" />
      <FormGrid>
        <Field label="Service schedule" optional hint="what is due, and how often">
          {({ id, describedBy }) => (
            <SelectInput
              id={id}
              describedBy={describedBy}
              label="Service schedule"
              value={form.serviceTemplateId || null}
              placeholder="No schedule"
              clearable
              clearLabel="No schedule"
              onChange={(value) => set({ serviceTemplateId: value ?? '' })}
              options={(lookups?.templates ?? []).map((template) => ({
                value: template.id, label: template.name,
              }))}
            />
          )}
        </Field>

        <Field
          label="Paired with"
          optional
          hint="a trailer that follows its truck — assigning one assigns the other"
        >
          {({ id, describedBy }) => (
            <SelectInput
              id={id}
              describedBy={describedBy}
              label="Paired with"
              value={form.pairedWithId || null}
              placeholder="Not paired"
              clearable
              clearLabel="Not paired"
              onChange={(value) => set({ pairedWithId: value ?? '' })}
              options={pairable.map((piece) => ({
                value: piece.id, label: `${piece.name} · ${piece.assetTag}`,
              }))}
            />
          )}
        </Field>
      </FormGrid>

      {/* --- the note --------------------------------------------------- */}
      <FormSection title="Anything else" />
      <FormGrid>
        <Wide>
          <Field label="Notes" optional hint="what the next person to pick it up should know">
            {({ id, describedBy }) => (
              <TextArea
                id={id}
                describedBy={describedBy}
                rows={3}
                value={form.notes}
                placeholder="Second key is in the shop. Right rear tyre reads low and is fine."
                onChange={(event) => set({ notes: event.target.value })}
              />
            )}
          </Field>
        </Wide>
      </FormGrid>

      <p className="m-0 flex items-start gap-1.5 text-[12px] leading-[1.45] text-text-muted">
        <span className="mt-0.5 shrink-0 text-text-faint"><IconMachine size={13} /></span>
        A piece cannot be on two visits at once — the schedule enforces that, not this form.
      </p>
    </Sheet>
  )
}

/** What the type's meter is, in words somebody would say. */
const meterWords = (kind: string): string => kind === 'miles' ? 'reads miles'
  : kind === 'hours' ? 'reads hours'
    : 'no meter'

/** "12500.0000" is a database's way of saying 12500. */
const trim = (value: string | null): string => {
  if (value === null || value === '') return ''
  const asNumber = Number(value)
  return Number.isFinite(asNumber) ? String(asNumber) : value
}

const number = (value: string): number | null => {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const asNumber = Number(trimmed)
  return Number.isFinite(asNumber) ? asNumber : null
}
